import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";

/**
 * GET /admin/stripe/connection/status
 * @typedef {Object} StripeConnectionStatus
 * @property {boolean} is_connected
 * @property {string|null} stripe_customer_id
 * @property {string|null} connected_at
 * @property {boolean} has_default_payment_method
 * @property {boolean} free_period_active - Legacy location-subscription field.
 * @property {string|null} free_period_until - Legacy location-subscription field.
 */

/**
 * A saved card from /admin/stripe/cards.
 * @typedef {Object} StripePaymentCard
 * @property {string} id - Stripe payment method id, e.g. "pm_1Qxxxxx".
 * @property {string} type
 * @property {{brand: string, last4: string, exp_month: number, exp_year: number,
 *   country?: string, funding?: string}} card
 * @property {boolean} is_default
 * @property {string} created_at - "Y-m-d H:i:s", not ISO-8601.
 */

/**
 * GET /admin/stripe/cards
 * @typedef {Object} StripeCardsResponse
 * @property {StripePaymentCard[]} cards
 * @property {number} [total]
 * @property {string|null} [default_payment_method]
 * @property {string} [message] - Present when the org is not connected.
 */

/**
 * POST /admin/stripe/cards
 * @typedef {{ payment_method_id: string }} AddStripeCardRequest
 */

/**
 * PUT /admin/stripe/cards/default
 * @typedef {{ payment_method_id: string }} SetDefaultStripeCardRequest
 */

const initialState = {
  connectionStatus: null,
  connectionChecked: false,
  stripeCustomerId: "",
  connectedAt: null,
  hasDefaultPaymentMethod: false,
  freePeriodActive: false,
  freePeriodUntil: null,
  publicKey: "",
  cards: [],
  cardsTotal: 0,
  defaultPaymentMethod: null,
  cardsLoaded: false,
  status: "idle",
  error: null,
};

const { httpRequest } = Http();

// Get Stripe connection status
export const getStripeConnectionStatus = createAsyncThunk(
  "getStripeConnectionStatus",
  async ({ navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: "admin/stripe/connection/status",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch Stripe connection status", {
        cause: err.cause,
      });
    }
  }
);

// Connect Stripe account
export const connectStripe = createAsyncThunk(
  "connectStripe",
  async ({ navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: "admin/stripe/connect",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to connect Stripe", {
        cause: err.cause,
      });
    }
  }
);

// Get list of saved cards
export const getCards = createAsyncThunk(
  "getCards",
  async ({ navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: "admin/stripe/cards",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch cards", {
        cause: err.cause,
      });
    }
  }
);

// Add new card
export const addCard = createAsyncThunk(
  "addCard",
  async ({ paymentMethodId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: "admin/stripe/cards",
          body: { payment_method_id: paymentMethodId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to add card", {
        cause: err.cause,
      });
    }
  }
);

// Set default card
export const setDefaultCard = createAsyncThunk(
  "setDefaultCard",
  async ({ paymentMethodId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "PUT",
          url: "admin/stripe/cards/default",
          body: { payment_method_id: paymentMethodId },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to set default card", {
        cause: err.cause,
      });
    }
  }
);

// Delete card
export const deleteCard = createAsyncThunk(
  "deleteCard",
  async ({ paymentMethodId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/stripe/cards/${paymentMethodId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to delete card", {
        cause: err.cause,
      });
    }
  }
);

const paymentMethodSlice = createSlice({
  name: "paymentMethod",
  initialState,
  reducers: {
    showToast: (state, action) => {
      const { success, message } = action.payload;
      toastHandler(success, message);
    },
    resetPaymentMethodState: (state) => {
      state.cards = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            getStripeConnectionStatus.pending,
            connectStripe.pending,
            getCards.pending,
            addCard.pending,
            setDefaultCard.pending,
            deleteCard.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error = null;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(true);
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            getStripeConnectionStatus.fulfilled,
            connectStripe.fulfilled,
            getCards.fulfilled,
            addCard.fulfilled,
            setDefaultCard.fulfilled,
            deleteCard.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";
          const actionType = action.type.split("/")[0];

          if (actionType === "getStripeConnectionStatus") {
            state.connectionStatus =
              action.payload?.connected || action.payload?.is_connected || false;
            state.connectionChecked = true;
            state.stripeCustomerId =
              action.payload?.stripe_customer_id || state.stripeCustomerId;
            state.connectedAt = action.payload?.connected_at || null;
            state.hasDefaultPaymentMethod =
              action.payload?.has_default_payment_method || false;
            // Legacy location-subscription fields, kept for existing callers.
            state.freePeriodActive =
              action.payload?.free_period_active || false;
            state.freePeriodUntil =
              action.payload?.free_period_until || null;
            state.publicKey = action.payload?.public_key || state.publicKey;
          }

          if (actionType === "connectStripe") {
            // The endpoint is idempotent: 201 on first connect, 200 after.
            state.connectionStatus =
              action.payload?.is_connected !== undefined
                ? Boolean(action.payload.is_connected)
                : true;
            state.connectionChecked = true;
            state.stripeCustomerId =
              action.payload?.stripe_customer_id || state.stripeCustomerId;
            state.connectedAt =
              action.payload?.connected_at || state.connectedAt;
            state.publicKey = action.payload?.public_key || state.publicKey;
          }

          if (actionType === "getCards") {
            state.cards = action.payload?.cards || action.payload?.data || [];
            state.cardsTotal =
              action.payload?.total ?? state.cards.length;
            state.defaultPaymentMethod =
              action.payload?.default_payment_method || null;
            state.cardsLoaded = true;
            state.publicKey = action.payload?.public_key || state.publicKey;
          }

          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }

          // connectStripe is deliberately absent: DashboardLayout auto-connects
          // in the background, so its toast is raised by the screen that the
          // user actually clicked Connect on.
          if (["addCard", "setDefaultCard", "deleteCard"].includes(actionType)) {
            const successMessages = {
              addCard: "Card added successfully",
              setDefaultCard: "Default card updated successfully",
              deleteCard: "Card deleted successfully",
            };
            paymentMethodSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message: action?.payload?.message || successMessages[actionType],
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            getStripeConnectionStatus.rejected,
            connectStripe.rejected,
            getCards.rejected,
            addCard.rejected,
            setDefaultCard.rejected,
            deleteCard.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }
          paymentMethodSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export const { resetPaymentMethodState } = paymentMethodSlice.actions;
export default paymentMethodSlice.reducer;
